import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, LifeBuoy, Loader2, Mail, User, X } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { servexaPreLoginConfig, servexaPreLoginResult } from '../lib/types'
import { descriptionSchema, emailSchema, nameSchema } from '../lib/validators'

/**
 * [Servexa] Pre-login "Report an Issue" (spec A6a).
 *
 * The people who need this are exactly the ones who CANNOT sign in — a broken
 * Sign In, a failed Sign Up, a stuck reset/activation, an OTP that won't verify.
 * So it must be reachable with NO session and must never touch the secret
 * integration key: the browser posts to our own backend relay
 * (`POST /api/v1/servexa/pre-login-issues`), which attaches the non-secret
 * `publicIssueCode` server-side. This component only ever sees `{ enabled }`.
 *
 * Rendered once from `AuthLayout`, so it appears on every auth screen (Sign In /
 * Sign Up / Forgot Password / Reset / Join). It renders NOTHING until the
 * backend confirms the flow is configured, so an unconfigured deployment shows
 * no dead button.
 *
 * The form deliberately mirrors the Sign Up (register) form: the same shared
 * validators (`nameSchema` / `emailSchema` / `descriptionSchema`), the same
 * react-hook-form + zodResolver wiring, the same "Full Name" / "Email ID"
 * labels, the same "Enter …" placeholders, and the same icon-prefixed field
 * styling — so it reads as a native part of the auth screens, not a bolt-on.
 */

// The exact A6a issue types, shown with plain-English labels.
const ISSUE_TYPES = [
  { value: 'SIGN_IN', label: "Can't sign in" },
  { value: 'SIGN_UP', label: "Can't sign up" },
  { value: 'FORGOT_PASSWORD', label: 'Password reset not working' },
  { value: 'ACCOUNT_ACTIVATION', label: 'Account activation issue' },
  { value: 'OTP_VERIFICATION', label: 'OTP / verification code issue' },
  { value: 'AUTH_SESSION', label: 'Signed out unexpectedly' },
  { value: 'OTHER', label: 'Something else' },
]
const ISSUE_TYPE_VALUES = ISSUE_TYPES.map((t) => t.value)

// Same shared schemas the register form uses — so name/email rules and their
// messages are byte-identical across both forms. `strictTld` matches Sign Up.
const schema = z.object({
  reporterName: nameSchema('Full Name'),
  reporterEmail: emailSchema({ strictTld: true }),
  issueType: z.enum(ISSUE_TYPE_VALUES, { message: 'Please select what went wrong.' }),
  description: descriptionSchema(),
  // Honeypot (A6a) — never validated as a real field; a real user leaves it
  // empty and a bot that fills it gets an upstream silent no-op.
  website: z.string().optional(),
})

export function PreLoginSupportCallout() {
  const [enabled, setEnabled] = useState(false)
  const [open, setOpen] = useState(false)

  // One-shot config probe. Fetch in a callback (never a synchronous effect
  // body), and swallow failures — a probe error just leaves the callout hidden.
  useEffect(() => {
    let alive = true
    api
      .get('/servexa/pre-login-issues/config')
      .then((res) => {
        if (!alive) return
        const parsed = servexaPreLoginConfig.safeParse(res.data)
        setEnabled(parsed.success ? parsed.data.enabled : false)
      })
      .catch(() => {
        if (alive) setEnabled(false)
      })
    return () => {
      alive = false
    }
  }, [])

  if (!enabled) return null

  return (
    <>
      <div className="mt-6 rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 flex items-start gap-3 shadow-sm">
        <span className="shrink-0 mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
          <LifeBuoy size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
            Can&apos;t access your account?
          </p>
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)]">
            Report an issue and our support team can help — no sign-in needed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
        >
          Report an issue
        </button>
      </div>
      {open ? <PreLoginSupportModal onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function PreLoginSupportModal({ onClose }) {
  const [done, setDone] = useState(null) // { ticketNumber } | null

  const form = useForm({
    // `onTouched` — validate a field once it's blurred, then on every change
    // after. Same mode the Sign Up form uses.
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: {
      reporterName: '',
      reporterEmail: '',
      issueType: '',
      description: '',
      website: '',
    },
  })
  const errors = form.formState.errors

  // Escape closes; backdrop click does NOT (matches the app's form-modal rule).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function onSubmit(values) {
    try {
      const res = await api.post('/servexa/pre-login-issues', {
        reporterName: values.reporterName.trim(),
        reporterEmail: values.reporterEmail.trim(),
        issueType: values.issueType,
        description: values.description.trim(),
        // Only send the honeypot when (abnormally) filled, so a normal request
        // stays byte-for-byte what Servexa expects.
        ...(values.website ? { website: values.website } : {}),
      })
      // Any 2xx is success (A6a): the ticketNumber may be null on the silent
      // honeypot path, which a real client cannot and need not distinguish.
      const parsed = servexaPreLoginResult.safeParse(res.data)
      const ticketNumber = parsed.success ? parsed.data.ticketNumber ?? null : null
      setDone({ ticketNumber: ticketNumber ?? null })
    } catch (err) {
      // Generic, safe failure copy — never leak why (A6a security model).
      form.setError('root', {
        message:
          extractApiError(err).message || 'Could not send your report. Please try again.',
      })
    }
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
      <div className="w-[480px] max-w-full rounded-2xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl text-[color:var(--color-ink)]">
        <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-center justify-between">
          <p className="font-semibold text-[color:var(--color-ink-strong)] flex items-center gap-2">
            <LifeBuoy size={16} className="text-[color:var(--color-accent-strong)]" />
            Report an issue
          </p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
              Thanks — your report was sent.
            </p>
            <p className="mt-1.5 text-sm text-[color:var(--color-ink-muted)]">
              {/* No promised email: Servexa never sends automated emails to a
               *  reporter — only a support agent may choose to (spec A1). */}
              {done.ticketNumber
                ? `Your reference is ${done.ticketNumber}. Our support team will look into it and may contact you at the email address you gave.`
                : 'Our support team will look into it and may contact you at the email address you gave.'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <div className="px-5 py-4 space-y-4">
              {errors.root ? (
                <p role="alert" className="text-xs text-[color:var(--color-danger-text)]">
                  {errors.root.message}
                </p>
              ) : null}
              <IconInput
                id="reporterName"
                label="Full Name"
                required
                icon={User}
                autoComplete="name"
                placeholder="Enter the Full Name"
                maxLength={100}
                error={errors.reporterName?.message}
                register={form.register('reporterName')}
              />
              <IconInput
                id="reporterEmail"
                label="Email ID"
                required
                type="email"
                icon={Mail}
                autoComplete="email"
                placeholder="Enter the Email ID"
                maxLength={254}
                error={errors.reporterEmail?.message}
                register={form.register('reporterEmail')}
              />
              <SelectField
                id="issueType"
                label="What went wrong?"
                required
                icon={AlertCircle}
                error={errors.issueType?.message}
                register={form.register('issueType')}
              >
                <option value="">Select an option</option>
                {ISSUE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </SelectField>
              <TextAreaInput
                id="description"
                label="Description"
                required
                placeholder="Enter a Description"
                maxLength={1000}
                rows={4}
                error={errors.description?.message}
                register={form.register('description')}
              />
              {/* Honeypot — invisible to humans, off the tab order, not announced. */}
              <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
                <label htmlFor="website">Website</label>
                <input id="website" type="text" tabIndex={-1} autoComplete="off" {...form.register('website')} />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[color:var(--color-rule)] flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 rounded-lg text-sm border border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  'Send report'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

/** Icon-prefixed text input — mirrors the Sign Up form's `IconInput`
 *  (h-11, rounded-xl, focus:border-primary, danger-text error) so both forms
 *  read identically. */
function IconInput({ id, label, required, icon: Icon, type = 'text', autoComplete, placeholder, maxLength, error, register }) {
  return (
    <div>
      <FieldLabel id={id} label={label} required={required} />
      <div className="relative">
        <Icon
          size={16}
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
        />
        <input
          id={id}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-invalid={Boolean(error)}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={inputClass(error, 'pl-10 pr-3')}
          {...register}
        />
      </div>
      <FieldError id={id} error={error} />
    </div>
  )
}

function SelectField({ id, label, required, icon: Icon, error, register, children }) {
  return (
    <div>
      <FieldLabel id={id} label={label} required={required} />
      <div className="relative">
        {Icon ? (
          <Icon
            size={16}
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
          />
        ) : null}
        <select
          id={id}
          aria-invalid={Boolean(error)}
          aria-required={required || undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={'fd-select ' + inputClass(error, (Icon ? 'pl-10' : 'pl-3') + ' pr-3 cursor-pointer')}
          {...register}
        >
          {children}
        </select>
      </div>
      <FieldError id={id} error={error} />
    </div>
  )
}

function TextAreaInput({ id, label, required, placeholder, maxLength, rows = 4, error, register }) {
  return (
    <div>
      <FieldLabel id={id} label={label} required={required} />
      <textarea
        id={id}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={Boolean(error)}
        aria-required={required || undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={
          'w-full px-3 py-2 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] resize-y ' +
          (error
            ? 'border-[color:var(--color-priority-urgent)]'
            : 'border-[color:var(--color-rule)]')
        }
        {...register}
      />
      <FieldError id={id} error={error} />
    </div>
  )
}

function FieldLabel({ id, label, required }) {
  return (
    <label
      htmlFor={id}
      className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
    >
      {label}
      {required ? (
        <span aria-hidden="true" className="ml-0.5 text-[color:var(--color-priority-urgent)]">
          *
        </span>
      ) : null}
    </label>
  )
}

function FieldError({ id, error }) {
  if (!error) return null
  return (
    <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-[color:var(--color-danger-text)]">
      {error}
    </p>
  )
}

function inputClass(error, pad) {
  return (
    `w-full h-11 ${pad} rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ` +
    (error ? 'border-[color:var(--color-priority-urgent)]' : 'border-[color:var(--color-rule)]')
  )
}
